/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { CosmosPagination } from "@components/elements/molecules/pagination/CosmosPagination";
import { useDirectoryContext } from "../core/Directory.context";

export interface DirectoryPaginationProps {
	onPageScroll?: () => void;
}

/**
 * Pagination slot. Wraps `CosmosPagination` and threads the Directory's current page
 * + total through. Passes `scrollTop={false}` — scroll is driven by the optional
 * `onPageScroll` prop, called alongside `setPage` in `handlePageChange`.
 *
 * Hidden when there's only one page (matching existing Cosmos convention).
 *
 * A11y: rendered inside a `<nav>` landmark so AT users get a recognizable
 * "Pagination" rotor target. Label sourced from `config.paginationRegionLabel`
 * (English fallback "Pagination" — set to a localized string at the call site
 * when shipping a non-en-US locale).
 */
export function DirectoryPagination({ onPageScroll }: DirectoryPaginationProps): React.JSX.Element | null {
	const { config, result } = useDirectoryContext();
	const perPage = config.pagination?.perPage;
	if (!perPage) return null;
	if (result.totalPages <= 1) return null;

	return (
		<nav aria-label={config.paginationRegionLabel ?? "Pagination"}>
			<CosmosPagination
				current={result.currentPage}
				pageSize={perPage}
				total={result.totalFilteredCount}
				handlePageChange={(page) => {
					result.setPage(page);
					onPageScroll?.();
				}}
				scrollTop={false}
			/>
		</nav>
	);
}
